/**
 * Category Field Default Configuration
 *
 * Extremely complex field with category details, product listing,
 * product details, and sorting options. This is the second most
 * complex field type after multichoice.
 */

// ============================================================================
// Type Definitions
// ============================================================================

export type CategoryStyle = 'Grid' | 'List';
export type ImageDisplay = 'Fit' | 'Fill' | 'Stretch';
export type ProductsLoading = 'Infinite Scroll' | 'Load More Button';
export type DescriptionStyle = 'Partly' | 'Full' | 'Collapsed';
export type TextAlignment = 'Left' | 'Centre' | 'Right';
export type PdpLaunchIn = 'New Window' | 'New Tab';

export interface CategoryFieldConfig {
  // Category Details Section
  label: string;
  showTitle: boolean;
  titleText: string;
  showCategoryDescription: boolean;
  descriptionText: string;
  showThumbnail: boolean;
  imageDisplay: ImageDisplay;
  categoryImageUrl: string;
  hideIfNoProducts: boolean;
  selectByDefault: boolean;

  // Product Listing Section
  style: CategoryStyle;
  productsPerRow: string;
  productsLoading: ProductsLoading;
  scrollBarAfterRows: string;
  showButtonAfterProducts: string;
  scrollBarAfterProducts: string;

  // Product Details Section
  showModelNumber: boolean;
  showProductDescription: boolean;
  descriptionStyle: DescriptionStyle;
  showPrice: boolean;
  showUOM: boolean;
  showAvailabilityStatus: boolean;
  inStockLabel: string;
  backOrderLabel: string;
  showVariants: boolean;
  showNumberOfVariants: boolean;
  showCategory: boolean;
  showManufacturer: boolean;
  pdpLaunchOnTitleClick: boolean;
  pdpLaunchOnImageClick: boolean;
  pdpLaunchIn: PdpLaunchIn;
  productDetailsTextAlignment: TextAlignment;

  // Button
  showButtonText: boolean;
  buttonLabel: string;
  showButtonIcon: boolean;
  buttonPlacement: 'Left' | 'Centre' | 'Right';
  buttonIconUrl: string;

  // Advanced Section
  showInStock: boolean;
  inStockText: string;
  attributes: string[];
}

// ============================================================================
// Default Configuration
// ============================================================================

export const CATEGORY_DEFAULT_CONFIG: Omit<CategoryFieldConfig, 'label'> = {
  // Category Details
  showTitle: true,
  titleText: '',
  showCategoryDescription: true,
  descriptionText: 'Compatibility based on selections in your build',
  showThumbnail: true,
  imageDisplay: 'Fit',
  categoryImageUrl: '',
  hideIfNoProducts: false,
  selectByDefault: false,
  // Product Listing
  style: 'Grid',
  productsPerRow: '3',
  productsLoading: 'Infinite Scroll',
  scrollBarAfterRows: '5',
  showButtonAfterProducts: '50',
  scrollBarAfterProducts: '20',
  // Product Details
  showModelNumber: true,
  showProductDescription: true,
  descriptionStyle: 'Partly',
  showPrice: true,
  showUOM: false,
  showAvailabilityStatus: true,
  inStockLabel: 'In Stock',
  backOrderLabel: 'Back Order',
  showVariants: false,
  showNumberOfVariants: false,
  showCategory: false,
  showManufacturer: false,
  pdpLaunchOnTitleClick: true,
  pdpLaunchOnImageClick: false,
  pdpLaunchIn: 'New Window',
  productDetailsTextAlignment: 'Left',
  // Button
  showButtonText: true,
  buttonLabel: 'Add to Cart',
  showButtonIcon: false,
  buttonPlacement: 'Left',
  buttonIconUrl: '',
  // Advanced
  showInStock: true,
  inStockText: 'Show In Stock only',
  attributes: [],
};

// ============================================================================
// Factory Function
// ============================================================================

/**
 * Create default configuration for a category field
 * @param fieldName The field name to use as label/title
 */
export const createCategoryConfig = (fieldName: string): CategoryFieldConfig => ({
  ...CATEGORY_DEFAULT_CONFIG,
  label: fieldName,
  titleText: '',
});

// ============================================================================
// Style-Specific Default Overrides
// ============================================================================

/**
 * Get style-specific default overrides
 * Grid and List views have different sensible defaults
 */
export const getStyleDefaults = (style: CategoryStyle): Partial<CategoryFieldConfig> => {
  switch (style) {
    case 'Grid':
      return {
        productsPerRow: '3',
        productDetailsTextAlignment: 'Centre',
      };
    case 'List':
      return {
        productsPerRow: '1',
        productDetailsTextAlignment: 'Left',
      };
    default:
      return {};
  }
};

/**
 * Create configuration for a specific style
 */
export const createCategoryConfigForStyle = (
  fieldName: string,
  style: CategoryStyle
): CategoryFieldConfig => ({
  ...createCategoryConfig(fieldName),
  style,
  ...getStyleDefaults(style),
});

export default createCategoryConfig;
